import React, { useState, useRef, useEffect } from 'react';
import { compressMessageImage } from '../../utils/imageCompression';
import { encryptMedia } from '../../utils/mediaEncryption';

interface ChatInputProps {
  onSend: (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string }) => Promise<void>;
  disabled?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

async function uploadBlob(blob: Blob, filename: string): Promise<{ mediaType: string; mediaUrl: string; thumbnailUrl?: string }> {
  const formData = new FormData();
  formData.append('media', blob, filename);
  const res = await fetch(`${API_BASE_URL}/api/messages/upload-media`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Upload failed');
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Upload failed');
  return data.data;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const handleSend = async () => {
    if (!message.trim()) return;
    try {
      await onSend(message.trim());
      setMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only images (JPG, PNG, WebP, GIF) and videos (MP4, MOV) are allowed.');
      return;
    }

    const maxVideoSize = 50 * 1024 * 1024;
    if (file.type.startsWith('video/') && file.size > maxVideoSize) {
      alert('Video too large. Maximum size is 50MB.');
      return;
    }

    setIsUploading(true);
    try {
      let fileToUpload: Blob = file;
      if (file.type.startsWith('image/')) {
        fileToUpload = await compressMessageImage(file);
      }
      const { encryptedBlob, key, mimeType } = await encryptMedia(fileToUpload);
      const mediaData = await uploadBlob(encryptedBlob, file.name);
      await onSend('', { ...mediaData, mediaKey: key, mediaMimeType: mimeType });
    } catch (error) {
      console.error('Failed to upload media:', error);
      alert('Failed to upload media. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start(100);
      setIsRecording(true);
      setRecordingSeconds(0);

      timerRef.current = setInterval(() => {
        setRecordingSeconds(s => {
          if (s >= 119) {
            stopRecording(true);
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      alert('Microphone access denied. Please allow microphone access and try again.');
    }
  };

  const stopRecording = async (send: boolean) => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (send) {
      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setIsRecording(false);
        setRecordingSeconds(0);

        if (blob.size < 1000) return; // ignore accidental taps

        setIsUploading(true);
        try {
          const { encryptedBlob, key, mimeType } = await encryptMedia(blob);
          const mediaData = await uploadBlob(encryptedBlob, `voice-${Date.now()}.${ext}`);
          await onSend('', { ...mediaData, mediaType: 'audio', mediaKey: key, mediaMimeType: mimeType });
        } catch (err) {
          alert('Failed to send voice message. Please try again.');
        } finally {
          setIsUploading(false);
        }
      };
      recorder.stop();
    } else {
      recorder.onstop = () => {};
      recorder.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      setIsRecording(false);
      setRecordingSeconds(0);
    }
  };

  const formatTimer = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const isEmpty = !message.trim();

  return (
    <div className="card" style={{ borderRadius: '0', borderTop: '1px solid var(--border-color)' }}>
      <div className="card-content" style={{ padding: '12px 16px' }}>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,video/*"
          style={{ display: 'none' }}
        />
        {/* capture="environment" with no other options traps the user — so we
            use a plain image input here; the action menu below provides the
            "Take Photo" vs "Library" choice with a dismissible Cancel option */}
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
        />

        {/* Media action menu */}
        {showMediaMenu && (
          <>
            <div
              onClick={() => setShowMediaMenu(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 100 }}
            />
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              zIndex: 101,
              backgroundColor: 'var(--background-paper)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              minWidth: '180px',
              marginBottom: '8px'
            }}>
              {[
                { icon: 'photo_camera', label: 'Take Photo', action: () => { setShowMediaMenu(false); cameraInputRef.current?.click(); } },
                { icon: 'photo_library', label: 'Choose from Library', action: () => { setShowMediaMenu(false); fileInputRef.current?.click(); } },
                { icon: 'close', label: 'Cancel', action: () => setShowMediaMenu(false) },
              ].map(({ icon, label, action }) => (
                <div
                  key={label}
                  onClick={action}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', cursor: 'pointer', borderBottom: label !== 'Cancel' ? '1px solid var(--border-color)' : 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span className="material-icons" style={{ fontSize: '20px', color: label === 'Cancel' ? 'var(--error-color)' : 'var(--text-secondary)' }}>{icon}</span>
                  <span style={{ fontSize: '14px', color: label === 'Cancel' ? 'var(--error-color)' : 'var(--text-primary)' }}>{label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ position: 'relative' }}>
        {isRecording ? (
          /* Recording bar */
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Cancel */}
            <button
              className="icon-btn"
              onClick={() => stopRecording(false)}
              title="Cancel"
              style={{ color: 'var(--text-secondary)', padding: '8px' }}
            >
              <span className="material-icons">delete</span>
            </button>

            {/* Pulsing dot + timer */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--background-paper)' }}>
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: 'var(--error-color)',
                animation: 'pulse 1s infinite'
              }} />
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
              <span style={{ fontSize: '14px', color: 'var(--error-color)', fontVariantNumeric: 'tabular-nums' }}>
                {formatTimer(recordingSeconds)}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Recording...</span>
            </div>

            {/* Send */}
            <button
              className="btn"
              onClick={() => stopRecording(true)}
              disabled={isUploading}
              style={{
                padding: '10px 16px',
                background: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500'
              }}
            >
              <span className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>
                {isUploading ? 'hourglass_empty' : 'send'}
              </span>
            </button>
          </div>
        ) : (
          /* Normal input bar */
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            {/* Attach file */}
            <button
              className="icon-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading}
              title="Attach media"
              style={{ color: 'var(--text-secondary)', padding: '8px' }}
            >
              <span className="material-icons">attach_file</span>
            </button>

            {/* Camera — opens action menu so user can cancel */}
            <button
              className="icon-btn"
              onClick={() => setShowMediaMenu(m => !m)}
              disabled={disabled || isUploading}
              title="Take photo"
              style={{ color: showMediaMenu ? 'var(--primary-color)' : 'var(--text-secondary)', padding: '8px' }}
            >
              <span className="material-icons">photo_camera</span>
            </button>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              disabled={disabled || isUploading}
              rows={1}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                resize: 'none',
                fontFamily: 'inherit',
                fontSize: '14px',
                color: 'var(--text-primary)',
                background: 'var(--background-paper)',
                minHeight: '40px',
                maxHeight: '120px',
                overflowY: 'auto'
              }}
            />

            {isEmpty ? (
              /* Mic button — shown when nothing typed */
              <button
                className="icon-btn"
                onClick={startRecording}
                disabled={disabled || isUploading}
                title="Record voice message"
                style={{
                  padding: '10px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span className="material-icons">mic</span>
              </button>
            ) : (
              /* Send button — shown when text is present */
              <button
                className="btn"
                onClick={handleSend}
                disabled={disabled || isUploading}
                style={{
                  padding: '10px 20px',
                  minWidth: '80px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: !disabled && !isUploading ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
              >
                {isUploading ? 'Sending...' : 'Send'}
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
